import React, { useEffect, useState, useCallback } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLocation, useNavigate } from 'react-router';
import { Worker, Viewer, ProgressBar } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import { pdfjs } from 'react-pdf';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/zoom/lib/styles/index.css';
import '@react-pdf-viewer/page-navigation/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import '@react-pdf-viewer/full-screen/lib/styles/index.css';

import { useTranslate } from '../../../components/TranslationText';
import CustomPopUp from '../../../components/CustomPopUp';
import {
    eFormButtonsContainer,
    eformFormButton,
    eformTerms,
    termsTitle,
} from '../../../common/cssclasses';
import { getCountries, getHorseColors } from '../../sharedAPI/lookUpApis';
import {
    getSemenById,
    getSemenOrderById,
    createSemenOrder,
    buildOrderFormData,
} from '../Api/semenMarketApi';
import SemenStallionInfoBar from '../Components/SemenStallionInfoBar';
import SemenMareDetails from '../Components/SemenMareDetails';
import SemenRacingResults from '../Components/SemenRacingResults';
import SemenCoveringLocation from '../Components/SemenCoveringLocation';
import SemenContractSection from '../Components/SemenContractSection';
import PaymentDocumentsForm from '../Components/PaymentDocumentsForm';
import { shouldShowPaymentDocs, OrderStatus } from '../constants/orderStatus';
import { buildSchema, defaultFormValues } from '../constants/checkoutSchema';
import SubmitServiceBtn from '../../../components/servicesButtons/SubmitServiceBtn';
import SemenReason from '../Components/SemenReason';


// ─── Component ─────────────────────────────────────────────────
export default function SemenCheckoutPage({ toast }) {
    const { translate } = useTranslate();
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const semenMarketId = searchParams.get('semenMarketId');
    const orderId = searchParams.get('orderId');
    const isVIPHorseParam = searchParams.get('isVip') === 'true';
    const isEditMode = !!orderId;

    // ─── React Hook Form ──────────────────────────────────────
    const methods = useForm({
        defaultValues: defaultFormValues,
        resolver: yupResolver(buildSchema(translate)),
        mode: 'onTouched',
        shouldFocusError: true, // Enabled by default
    });

    const {
        handleSubmit,
        watch,
        reset,
        setValue,
        formState: { isSubmitting },
    } = methods;

    const isRegisteredMare = watch('isRegisteredMare');
    const isForExport = watch('isForExport');

    // ─── Local State ──────────────────────────────────────────
    const [semenDetails, setSemenDetails] = useState(null);
    const [orderDetails, setOrderDetails] = useState(null);
    const [colors, setColors] = useState([]);
    const [countries, setCountries] = useState([]);
    const [selectedMare, setSelectedMare] = useState(null);

    const actionParam = searchParams.get('action'); // 'edit', 'view', or 'additionalDose'
    const isAdditionalDose = actionParam === 'additionalDose';

    // isBestHorse from API data, or fallback to URL param
    const isBestHorse = semenDetails?.isBestHorses ?? isVIPHorseParam;

    // Sync isBestHorse into the form so Yup's .test() can access it via this.parent
    useEffect(() => {
        setValue('isBestHorse', isBestHorse);
    }, [isBestHorse, setValue]);

    const isFormDisabled = React.useMemo(() => {
        if (!orderId) return false; // New order flow
        if (isAdditionalDose) return false; // Additional dose = new editable order
        if (actionParam === 'view') return true;
        if (actionParam === 'payment') return true; // Payment action disables core fields
        if (actionParam === 'edit') {
            const status = orderDetails?.status;
            // Only allow edit of core fields if status is Return
            if (status === OrderStatus.Return) {
                return false;
            }
            return true;
        }
        // Fallback if no action is provided: treat as default 'edit' permission check
        const status = orderDetails?.status;
        if (status === OrderStatus.Return) {
            return false;
        }
        return true;
    }, [actionParam, orderId, orderDetails, isAdditionalDose]);

    // ─── Lookup Data (skip in view mode — build from order details) ──
    useEffect(() => {
        if (actionParam === 'view') return;
        // additionalDose is not 'view', so lookups ARE fetched
        getHorseColors().then(setColors);
        getCountries().then(setCountries);
    }, [actionParam]);

    // In view mode, populate dropdown options from order details
    useEffect(() => {
        if (actionParam !== 'view' || !orderDetails) return;

        if (orderDetails.colorId) {
            setColors([{ value: orderDetails.colorId, text: orderDetails.colorName || orderDetails.color || '' }]);
        }
        if (orderDetails.originId) {
            setCountries((prev) => {
                const items = [...prev];
                if (!items.find((c) => c.value === orderDetails.originId)) {
                    items.push({ value: orderDetails.originId, text: orderDetails.originName || orderDetails.origin || '' });
                }
                if (orderDetails.coveringCountryId && !items.find((c) => c.value === orderDetails.coveringCountryId)) {
                    items.push({ value: orderDetails.coveringCountryId, text: orderDetails.coveringCountryName || orderDetails.coveringCountry || '' });
                }
                return items;
            });
        } else if (orderDetails.coveringCountryId) {
            setCountries([{ value: orderDetails.coveringCountryId, text: orderDetails.coveringCountryName || orderDetails.coveringCountry || '' }]);
        }
    }, [actionParam, orderDetails]);

    // ─── Fetch Semen Details ──────────────────────────────────
    useEffect(() => {
        if (semenMarketId) {
            getSemenById(semenMarketId).then(setSemenDetails);
        }
    }, [semenMarketId]);

    // ─── Fetch Order (edit mode) ──────────────────────────────
    useEffect(() => {
        if (orderId) {
            getSemenOrderById(orderId).then((order) => {
                if (!order) return;
                setOrderDetails(order);
                if (order.semenMarketId) {
                    getSemenById(order.semenMarketId).then(setSemenDetails);
                }

                reset({
                    isRegisteredMare: !!order.isRegisteredMare,
                    isForExport: !!order.isForExport,
                    mareId: order.mareId || '',
                    mareNameEn: order.mareNameEn || order.mareName || '',
                    mareNameAr: order.mareNameAr || order.mareName || '',
                    sireNameEn: order.sireNameEn || order.sireName || '',
                    sireNameAr: order.sireNameAr || order.sireName || '',
                    damNameEn: order.damNameEn || order.damName || '',
                    damNameAr: order.damNameAr || order.damName || '',
                    mareBirthDate: order.mareBirthDate
                        ? order.mareBirthDate.split('T')[0]
                        : '',
                    colorId: order.colorId || null,
                    originId: order.originId || null,
                    coveringCountryId: order.coveringCountryId || null,
                    centerName: order.centerName || '',
                    centerNumber: order.centerNumber || '',
                    externalComment: isAdditionalDose ? '' : (order.externalComment || ''),
                    orderStatus: isAdditionalDose ? OrderStatus.Return : (order.status || ''),
                    acceptContract: isAdditionalDose ? false : (order.status === OrderStatus.Paid || order.status === OrderStatus.Completed),
                    marePassport: order.marePassportFile?.fileUrl
                        ? [{ name: order.marePassportFile.name || 'passport', url: order.marePassportFile.fileUrl }]
                        : null,
                    paymentCertificate: !isAdditionalDose && order.status !== OrderStatus.PendingPayment && order.paymentCertificateFile?.fileUrl
                        ? [{ name: order.paymentCertificateFile.name || 'certificate', url: order.paymentCertificateFile.fileUrl }]
                        : null,
                    signedContract: !isAdditionalDose && order.status !== OrderStatus.PendingPayment && order.signedContractFile?.fileUrl
                        ? [{ name: order.signedContractFile.name || 'contract', url: order.signedContractFile.fileUrl }]
                        : null,
                    racingResults: order.racingResults?.length > 0
                        ? order.racingResults.map((rr) => ({
                            raceName: rr.raceName || '',
                            classification: rr.classification || '',
                            raceDate: rr.raceDate ? rr.raceDate.split('T')[0] : '',
                            rank: rr.rank ?? '',
                        }))
                        : [{ raceName: '', classification: '', raceDate: '', rank: '' }]
                });

                if (order.isRegisteredMare && order.mareId) {
                    setSelectedMare({
                        label: order.mareName || order.mareNameEn,
                        value: order.mareId,
                    });
                }
            });
        }
    }, [orderId, reset]);
    console.log('isBestHorse ', isBestHorse);
    // ─── Submit Handler ───────────────────────────────────────
    const onSubmit = useCallback(
        async (data) => {
            console.log('isBestHorse ', isBestHorse);

            try {
                const payload = {
                    ...data,
                    semenMarketId: semenMarketId || orderDetails?.semenMarketId,
                    ...(orderId && { orderId }),
                    ...(isAdditionalDose && { isAdditionalDose: true }),
                    ...(isRegisteredMare && selectedMare && { mareId: selectedMare.value }),
                };

                const formData = buildOrderFormData(payload, data.racingResults);
                await createSemenOrder(formData);

                toast.success(
                    translate('web.orderSubmitted', 'Order submitted successfully')
                );
                navigate('/profile?activeTab=semenRequests');
            } catch (err) {
                toast.error(
                    err?.response?.data?.message ||
                    translate('web.somethingWentWrong', 'Something went wrong')
                );
            }
        },
        [semenMarketId, orderId, orderDetails, isRegisteredMare, selectedMare, isBestHorse, isAdditionalDose, toast, translate, navigate]
    );

    // ─── Render ───────────────────────────────────────────────
    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} className="row">
                {/* ── Header ─────────────────────────────────── */}
                <h2 className={termsTitle + ' d-flex align-items-center flex-wrap mb-main'}>
                    {semenDetails?.regNr && semenDetails.regNr + ' - '}
                    {semenDetails?.horseName || translate('web.semenCheckout', 'Semen Checkout')}
                </h2>

                {/* ── Stallion Info Bar ───────────────────────── */}
                <SemenStallionInfoBar semenDetails={semenDetails} />

                {/* ── 1. Mare Details ─────────────────────────── */}
                <SemenMareDetails
                    colors={colors}
                    countries={countries}
                    disabled={isFormDisabled}
                    selectedMare={selectedMare}
                    onSelectedMareChange={setSelectedMare}
                />

                {/* ── 2. Racing Results ───────────────────────── */}
                <SemenRacingResults
                    disabled={isFormDisabled}
                    isBestHorse={isBestHorse}
                />

                {/* ── 3. Covering Location (always shown, required only when exporting) */}
                <SemenCoveringLocation
                    countries={countries}
                    disabled={isFormDisabled}
                />


                {/* ── Rejection/Return Reason ─────────────────── */}
                {isEditMode && !isAdditionalDose && orderDetails?.externalComment && (
                    <SemenReason orderDetails={orderDetails} />
                )}
                {/* ── Contract ─────────────────────────────── */}
                {isEditMode && !isAdditionalDose && (orderDetails?.status === OrderStatus.PendingPayment || orderDetails?.paymentCertificateFile?.fileUrl || orderDetails?.signedContractFile?.fileUrl) && (
                    <SemenContractSection
                        orderDetails={orderDetails}
                        disabled={actionParam === 'view'}
                    />
                )}

                {/* ── Payment Documents (conditional) ─────────── */}
                {isEditMode && !isAdditionalDose && (
                    orderDetails?.status === OrderStatus.PendingPayment
                        ? <PaymentDocumentsForm />
                        : (orderDetails?.paymentCertificateFile?.fileUrl || orderDetails?.signedContractFile?.fileUrl)
                            ? <PaymentDocumentsForm disabled={true} />
                            : null
                )}

                {/* ── Submit Button ──────────────────────────── */}
                {actionParam !== 'view' && (
                    <div className={`${eFormButtonsContainer} mt-4`}>
                        <SubmitServiceBtn
                            isButton={true}
                            type="submit"
                            disabled={isSubmitting}
                            text={translate('web.submit', 'Submit')}
                        />
                    </div>
                )}
            </form>
        </FormProvider>
    );
}

